import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { Room, Table, UserProfile } from '@shared/types';
import { User } from 'firebase/auth';
import { Header } from '../components/layout/Header';
import { TableCard } from '../components/public/TableCard';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorBoundary } from '../components/common/ErrorBoundary';

interface PublicTablesPageProps {
  userProfile?: UserProfile | null;
  user?: User | null; // Firebase User type
}

export function PublicTablesPage({ userProfile, user }: PublicTablesPageProps) {
  const { roomId } = useParams<{ roomId: string }>();
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [tables, setTables] = useState<Table[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // DIAGNOSTIC: Direct Firebase approach for room and tables
  useEffect(() => {
    if (!roomId) {
      setError(new Error('Room ID not provided'));
      setLoading(false);
      return;
    }

    if (!db) {
      setError(new Error('Firebase not initialized'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Fetch room details
    const fetchRoom = async () => {
      try {
        const roomRef = doc(db, 'rooms', roomId);
        const roomSnapshot = await getDoc(roomRef);
        
        if (!roomSnapshot.exists()) {
          throw new Error('Room not found');
        }
        
        const roomData = { id: roomSnapshot.id, ...roomSnapshot.data() } as Room;
        setRoom(roomData);
      } catch (err) {
        setError(err as Error);
        setLoading(false);
      }
    };

    // Fetch tables with real-time updates
    const setupTablesListener = () => {
      try {
        const tablesRef = collection(db, 'rooms', roomId, 'tables');

        const unsubscribe = onSnapshot(
          tablesRef,
          (snapshot) => {
            
            const tablesData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
            })) as Table[];

            setTables(tablesData);
            setLoading(false);
          },
          (err) => {
            setError(err as Error);
            setLoading(false);
          }
        );

        return unsubscribe;
      } catch (err) {
        setError(err as Error);
        setLoading(false);
        return () => {};
      }
    };

    // Fetch room and setup tables listener
    fetchRoom();
    const unsubscribeTables = setupTablesListener();

    return () => {
      if (unsubscribeTables) {
        unsubscribeTables();
      }
    };
  }, [roomId]);


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Room Not Found</h1>
          <p className="text-gray-600 mb-4">
            The requested poker room could not be found.
          </p>
          <Link to="/" className="btn btn-primary">
            Back to Rooms
          </Link>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        <Header userProfile={userProfile || null} user={user || null} />
        
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            {/* Room Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {room.name}
                  </h1>
                  <p className="mt-2 text-gray-600">{room.address}</p>
                  {room.phone && (
                    <p className="text-sm text-gray-500 mt-1">
                      📞 {room.phone}
                    </p>
                  )}
                  {room.websiteUrl && (
                    <a
                      href={room.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:text-primary/80 mt-1 inline-flex items-center"
                      title={`Visit ${room.websiteUrl}`}
                    >
                      🌐 Visit Website
                    </a>
                  )}
                </div>
                <Link to="/" className="btn btn-outline">
                  ← Back to Rooms
                </Link>
              </div>
            </div>

            {/* Tables Grid */}
            {!tables || tables.length === 0 ? (
              <div className="text-center py-12">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">No Tables Available</h2>
                <p className="text-gray-600">
                  There are currently no tables available at this poker room.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tables
                  .sort((a, b) => (a.display_order || 999) - (b.display_order || 999))
                  .map((table: Table) => (
                  <TableCard
                    key={table.id}
                    table={table}
                    roomId={roomId!}
                    onTableSelect={setSelectedTable}
                    selected={selectedTable?.id === table.id}
                    user={user || null}
                    userProfile={userProfile || null}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </ErrorBoundary>
  );
}
